import { Navigate, useLocation } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Box, Typography, Button } from "@mui/material";
import { useSelector } from "../store";
import useAuth from "../hooks/useAuth";
import LoadingScreen from "./LoadingScreen";
import useEntitlements from "../hooks/useEntitlements";
import useOrgSelectionModal from "../hooks/useOrgSelectionModal";
import { useOrganization } from "../pages/facility/hooks/useOrganization";
import {
  EXECUTIVE_ROLES,
  hasSubscriptionManagerRole,
} from "../utils/roleUtils";

interface RoleBasedRedirectProps {
  children?: React.ReactNode;
}

const RoleBasedRedirect = ({ children }: RoleBasedRedirectProps) => {
  const auth = useAuth();
  const { organization } = useOrganization();
  const flags = useFlags();
  const userRoles = useSelector((state) => state.user.roles);
  const rolesSuccess = useSelector((state) => state.user.rolesSuccess);
  const {
    entitlements,
    can,
    isOnboarded,
    isLoading: entitlementsLoading,
    error: entitlementsError,
  } = useEntitlements();
  const { openOrgSelectionModal } = useOrgSelectionModal();
  const location = useLocation();

  // Don't redirect if we're already on an auth route
  if (location.pathname.startsWith("/auth/")) {
    return <>{children}</>;
  }

  // Show loading screen while authentication is being initialized
  if (!auth.isInitialized) {
    return <LoadingScreen />;
  }

  // If not authenticated, let AuthGuard handle the redirect to login
  if (!auth.isAuthenticated) {
    return <>{children}</>;
  }

  if (entitlementsError) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          p: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="h4" color="error" gutterBottom>
          Unable to Load Your Organization&apos;s Access
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ mb: 4, maxWidth: 600 }}
        >
          We encountered a problem while verifying your organization&apos;s
          subscription and entitlements. The development team has been
          automatically notified.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Please try refreshing the page. If the problem persists, contact
          support.
        </Typography>
        <Button
          color="primary"
          variant="contained"
          sx={{ mt: 4 }}
          onClick={openOrgSelectionModal}
        >
          Home / Switch Organization
        </Button>
      </Box>
    );
  }

  // Show loading screen while user roles are being loaded
  if (
    rolesSuccess === undefined ||
    userRoles === undefined ||
    entitlementsLoading
  ) {
    return <LoadingScreen />;
  }

  // Org entitlement is not enough — sidebar and subscription-svc both require Executive.
  const hasExecutiveRole = userRoles?.some((role) =>
    EXECUTIVE_ROLES.includes(role),
  );
  const canAccessExecutiveDashboard =
    !!hasExecutiveRole &&
    can("feature_executive_dash") &&
    flags?.allowExecutiveDashboard;
  const baseRoute = canAccessExecutiveDashboard ? "/home/summary" : "/home";

  // If we're on the root path, redirect to the appropriate base route
  if (location.pathname === "/" || location.pathname === "") {
    return <Navigate to={baseRoute} replace />;
  }

  // Protect /home/summary from users who don't have executive role OR if the feature is disabled
  if (!canAccessExecutiveDashboard && location.pathname === "/home/summary") {
    return <Navigate to="/home" replace />;
  }

  // Handle 'needs_onboarding_payment' or 'isRestricted' redirect for managers
  const needsOnboardingPayment = isOnboarded("needs_onboarding_payment");
  const isRestricted = entitlements?.isRestricted;

  const isSubscriptionManager = hasSubscriptionManagerRole(userRoles);

  // Only Subscription Managers can sign Terms or manage payment methods.
  const forceBillingSetup =
    (needsOnboardingPayment || isRestricted) && isSubscriptionManager;

  if (forceBillingSetup) {
    const allowedPaths = ["/subscription", "/auth/logout", "/organization"];
    const isAllowedPath = allowedPaths.some(
      (path) =>
        location.pathname === path || location.pathname.startsWith(`${path}/`),
    );

    if (!isAllowedPath) {
      return <Navigate to="/subscription" replace />;
    }
  }

  // Display fullscreen message for regular users when restricted or cancelled
  if (isRestricted && !isSubscriptionManager) {
    const isCancelled = organization?.status === "cancelled";
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          p: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="h3" gutterBottom color="error">
          {isCancelled ? "Subscription Cancelled" : "Access Restricted"}
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ mb: 4, maxWidth: 600 }}
        >
          {isCancelled
            ? "Your organization's subscription has been cancelled and you can no longer use the application. Please contact your Subscription Manager to reactivate access."
            : "Your organization's access has been restricted due to outstanding billing invoices. Please contact your Subscription Manager to resolve the balance and restore access."}
        </Typography>
        <Button
          color="primary"
          variant="contained"
          onClick={openOrgSelectionModal}
        >
          Switch Organization
        </Button>
      </Box>
    );
  }

  return <>{children}</>;
};

export default RoleBasedRedirect;
